import { BaseAPI } from '../apis/amazon';
import { Webhook, WebhookLog } from 'utils/models/webhook.model';

// Helper to map Backend Log -> Frontend Log
const mapLog = (log: any): WebhookLog => ({
  webhookId: log.webhookId,
  logId: log.logId,
  eventType: log.eventType,
  statusCode: log.statusCode,
  latency: log.latency,
  success: log.status === 'SUCCESS' || log.statusCode >= 200 && log.statusCode < 300,
  timestamp: log.timestamp,
  payload: log.payload, // Mapping payload to payload for now as per contract drift
  error: log.status === 'FAILED' ? 'Delivery Failed' : undefined
});

// Helper to map Backend -> Frontend
const mapWebhook = (wh: any): Webhook => ({
  ...wh,
  name: wh.description || wh.url,
  enabled: wh.state === 'active',
  secret: wh.secret,
  logs: (wh.recent_logs || []).map(mapLog)
});

export const WebhookService = {
  getWebhooks: async () => {
    const res = await BaseAPI.get('/webhooks');
    const responseData = res.data as any;
    // Backend now returns { items: [...], maxActivePerEventType: N }
    const items = Array.isArray(responseData) ? responseData : (responseData.items || []);
    const maxActivePerEventType: number = responseData.maxActivePerEventType ?? 3;
    return {
      data: items.map(mapWebhook),
      maxActivePerEventType,
    };
  },

  getWebhook: async (id: string) => {
    const res = await BaseAPI.get(`/webhooks/${id}`);

    return { data: mapWebhook(res.data) };
  },

  createWebhook: async (data: Partial<Webhook>) => {
    // Map Frontend -> Backend
    const payload = {
      url: data.url,
      description: data.name, // Use name as description
      events: data.events || [],
      headers: data.headers || {}
    };

    const res = await BaseAPI.post('/webhooks', payload);
    return { data: mapWebhook(res.data) };
  },

  updateWebhook: async (id: string, data: Partial<Webhook>) => {
    // Map Frontend -> Backend
    const payload: any = {};
    if (data.url) payload.url = data.url;
    if (data.name) payload.description = data.name;
    if (data.events) payload.events = data.events;
    if (data.headers) payload.headers = data.headers;

    // Convert 'enabled' boolean to state enum if present
    if (data.state) payload.state = data.state;
    else if (typeof data.enabled !== 'undefined') {
      payload.state = data.enabled ? 'active' : 'inactive';
    }

    const res = await BaseAPI.put(`/webhooks/${id}`, payload);
    return { data: mapWebhook(res.data) };
  },

  deleteWebhook: async (id: string) => {
    await BaseAPI.delete(`/webhooks/${id}`);
    return { data: { message: 'Deleted' } };
  },

  testWebhook: async (id: string, eventType: string) => {
    const res = await BaseAPI.post(`/webhooks/${id}/test`, { eventType });
    return { data: res.data };
  },


};
